import React, { useState } from 'react';
import { Check, ArrowLeft, Menu, X } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useAgreement } from '../context/AgreementContext';

interface StepWizardProps {
  children: React.ReactNode;
}

const steps = [
  { id: 1, title: 'Purchaser Type', description: 'Select entity type' },
  { id: 2, title: 'Preferences', description: 'Property criteria' },
  { id: 3, title: 'Details', description: 'Contact information' },
  { id: 4, title: 'Agreement', description: 'Review & sign' }
];

export function StepWizard({ children }: StepWizardProps) {
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const { state, dispatch } = useAgreement();
  const { currentStep } = state;
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Calculate expiry date (30 days from now)
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  const daysUntilExpiry = Math.ceil((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const canNavigateToStep = (stepId: number) => {
    // Allow navigation to current step, any previous steps, and the furthest reached step
    return stepId <= Math.max(currentStep, state.furthestStep || currentStep);
  };

  const handleStepClick = (stepId: number) => {
    console.log("Step button clicked:", stepId, "canNavigate:", canNavigateToStep(stepId));
    if (canNavigateToStep(stepId)) {
      // Update the current step in the context
      dispatch({ type: 'SET_CURRENT_STEP', payload: stepId });
      // Also update furthest step if needed
      if (stepId > (state.furthestStep || 0)) {
        dispatch({ type: 'UPDATE_FURTHEST_STEP', payload: stepId });
      }
      setShowMobileMenu(false);
      console.log("Navigating to step:", stepId);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Compact Progress Header */}
      <div className="bg-white border-b flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-3">
            <Link
              to={`/${agreement_id}`}
              state={{ fromBackButton: true }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium hidden sm:inline">Back to Review</span>
              <span className="text-sm font-medium sm:hidden">Back</span>
            </Link>
            
            <div className="text-center flex-1 mx-4">
              <h1 className="text-lg font-semibold text-gray-900">
                Search Property Agreement
              </h1>
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-1">
                <span>Expires in {daysUntilExpiry} days</span>
                <span className="text-gray-300">•</span>
                <span>{formatDate(expiryDate)}</span>
              </div>
            </div>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Desktop Step Counter */}
            <div className="hidden md:block text-xs text-gray-500 whitespace-nowrap">
              Step {currentStep}/4
            </div>
          </div>
          
          {/* Desktop Progress Bar */}
          <div className="hidden md:flex items-center justify-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center">
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={!canNavigateToStep(step.id)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      step.id < Math.max(currentStep, state.furthestStep || currentStep)
                        ? 'bg-primary-500 border-primary-500 text-white cursor-pointer hover:bg-primary-600'
                        : step.id === currentStep
                        ? 'bg-primary-500 border-primary-500 text-white'
                        : step.id === (state.furthestStep || currentStep) && step.id > currentStep
                        ? 'bg-primary-100 border-primary-300 text-primary-600 cursor-pointer hover:bg-primary-200'
                        : canNavigateToStep(step.id)
                        ? 'bg-white border-gray-300 text-gray-400 cursor-pointer hover:border-gray-400'
                        : 'bg-white border-gray-300 text-gray-400 cursor-not-allowed'
                    }`}
                    aria-label={`Go to step ${step.id}: ${step.title}`}
                  >
                    {step.id < Math.max(currentStep, state.furthestStep || currentStep) ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <span className="text-xs font-medium">{step.id}</span>
                    )}
                  </button>
                </div>
                
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 sm:w-12 h-0.5 mx-1 transition-all duration-300 ${
                      step.id < Math.max(currentStep, state.furthestStep || currentStep) ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile Progress Bar (Simplified) */}
          <div className="md:hidden flex items-center justify-center">
            <div className="flex items-center gap-1">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    step.id < Math.max(currentStep, state.furthestStep || currentStep)
                      ? 'bg-primary-500'
                      : step.id === currentStep
                      ? 'bg-primary-500'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="ml-3 text-xs text-gray-500">
              {currentStep}/4
            </span>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-4 py-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Navigate to Step</h3>
              <div className="space-y-2">
                {steps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => handleStepClick(step.id)}
                    disabled={!canNavigateToStep(step.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                      step.id === currentStep
                        ? 'bg-primary-50 border-2 border-primary-200'
                        : step.id === (state.furthestStep || currentStep) && step.id > currentStep
                        ? 'bg-primary-25 border-2 border-primary-100'
                        : canNavigateToStep(step.id)
                        ? 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        : 'bg-gray-100 border-2 border-transparent opacity-50 cursor-not-allowed'
                    }`}
                    aria-label={`Go to step ${step.id}: ${step.title}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                        step.id < Math.max(currentStep, state.furthestStep || currentStep)
                          ? 'bg-primary-500 border-primary-500 text-white'
                          : step.id === currentStep
                          ? 'bg-primary-500 border-primary-500 text-white'
                          : step.id === (state.furthestStep || currentStep) && step.id > currentStep
                          ? 'bg-primary-100 border-primary-300 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-400'
                      }`}
                    >
                      {step.id < Math.max(currentStep, state.furthestStep || currentStep) ? (
                        <Check className="w-3 h-3" />
                      ) : (
                        <span className="text-xs font-medium">{step.id}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`font-medium text-sm ${
                        step.id === currentStep ? 'text-primary-900' : 'text-gray-900'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-gray-600">
                        {step.description}
                      </div>
                    </div>
                    {step.id === currentStep && (
                      <div className="text-xs text-primary-600 font-medium">
                        Current
                      </div>
                    )}
                    {step.id === (state.furthestStep || currentStep) && step.id > currentStep && (
                      <div className="text-xs text-primary-500 font-medium">
                        Continue
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step Content - Optimized for mobile */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 bg-gray-100 min-h-full">
        {children}
      </div>
    </div>
  );
}